import { head, indexOf, isArray, isEmpty, map, toNumber, union, uniqBy } from 'lodash';
import { broadcast } from '../event';
import type { ICollection } from './Collection.interface';

export default class Collection implements ICollection {
  Selection: Array<String>;
  Data: Array<Object>;
  Page: Number;
  Size: Number;

  get HasMore() {
    return this.Size === -1 || this.Data?.length < this.Size;
  }

  get PageCount() {
    return Math.ceil(this.Size / this.Page);
  }

  get PageCurrent() {
    return Math.ceil(this.Data?.length / this.Page);
  }

  get PageOffset() {
    const offset = this.Page * this.PageCurrent;
    const length = this.Data?.length;
    return offset > length ? offset : length;
  }

  append = (
    data?: Array<Object>,
    Model?: Object,
    ModelName?: String,
    uniqId: String = 'id'
  ) => {
    if (isEmpty(data)) {
      return data;
    }
    const className = ModelName || Model?.name || '';
    const objects = Model ? map(data, (obj) => new Model(obj)) : data;
    this.Data = uniqBy(union(this.Data, objects), uniqId);

    broadcast(`collection${className}`, 'appended', null, objects);
    return objects;
  };

  count = (data?: Number) => {
    if (!data) {
      this.Size = 0;
      return 0;
    }
    const size = toNumber(data);
    this.Size = size;
    return size;
  };

  isSelected = (id: String, selection?: Array<Object>) =>
    indexOf(selection || this.Selection, id) > -1;

  select = (id: String) => {
    if (!isArray(this.Selection)) {
      this.Selection = [];
    }

    if (id === 'ALL') {
      if (!this.Selection.length) {
        const len = this.Data?.length || 0;
        for (let i = 0; i < len; i += 1) {
          this.Selection.push(this.Data[i]?.id);
        }
      } else {
        this.Selection = [];
      }
    } else {
      const index = indexOf(this.Selection, id);
      const isSelected = index > -1;
      if (isSelected) {
        this.Selection.splice(index, 1);
      } else {
        this.Selection.push(id);
      }
    }
    return this.Selection;
  };

  /**
   * Constructor
   * @param {ICollection} data
   */
  constructor(data?: ICollection) {
    this.Selection = data?.Selection || [];
    this.Data = data?.Data || [];
    this.Page = data?.Page || 100;
    this.Size = data?.Size || -1;
  }
}
